export const HOUR = 60 * 60 * 1000;
export const DAY = 24 * HOUR;

/**
 * Google returns `{ dateTime }` for timed events and `{ date }` for all-day
 * ones. Date-only strings are parsed field by field so they land on local
 * midnight — `new Date("2026-09-10")` would read as UTC and shift the event to
 * the previous evening in Austin.
 */
export function parseGoogleDate(point) {
  if (point?.dateTime) return new Date(point.dateTime);
  if (point?.date) {
    const [y, m, d] = point.date.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  return null;
}

/**
 * One shape for every consumer: real Dates, an explicit `allDay` flag, and an
 * `end` that is either a Date later than `start` or null. Returns null for an
 * entry we can't place on the calendar at all.
 */
export function normalizeEvent(event) {
  const start = parseGoogleDate(event.start);
  if (!start) return null;

  const end = parseGoogleDate(event.end);

  return {
    id: event.id,
    title: event.summary || "Untitled event",
    start,
    end: end && end > start ? end : null,
    allDay: Boolean(event.start?.date),
    description: event.description,
    location: event.location,
    htmlLink: event.htmlLink,
  };
}

export const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * True for every day an event covers, not just the day it starts. Ends are
 * exclusive — an all-day event ending 09-14 runs through the 13th, and a show
 * running 10pm to midnight belongs to the day it started.
 */
export function occursOn(event, day) {
  const first = startOfDay(event.start).getTime();
  const last = startOfDay(
    event.end ? new Date(event.end.getTime() - 1) : event.start
  ).getTime();
  const target = startOfDay(day).getTime();

  return target >= first && target <= last;
}

/** All-day entries first, then chronological. */
export function compareEvents(a, b) {
  if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
  return a.start - b.start;
}

export function eventsOn(events, day) {
  return events.filter((event) => occursOn(event, day)).sort(compareEvents);
}

/**
 * The soonest event starting after `from`, so an empty day can point somewhere
 * instead of dead-ending. Null when there is nothing left on the calendar.
 */
export function nextUpcoming(events, from) {
  const after = startOfDay(from).getTime();
  return (
    events
      .filter((event) => event.start.getTime() > after)
      .sort((a, b) => a.start - b.start)[0] || null
  );
}

/** The inclusive last day an event covers, as a timestamp. */
function lastDayOf(event) {
  return startOfDay(
    event.end ? new Date(event.end.getTime() - 1) : event.start
  ).getTime();
}

/**
 * Everything still to come, chronologically. A multi-day event that has already
 * started but hasn't finished still counts as upcoming — it's running now.
 */
export function upcomingFrom(events, from) {
  const today = startOfDay(from).getTime();
  return events
    .filter((event) => lastDayOf(event) >= today)
    .sort((a, b) => {
      const byDay = startOfDay(a.start) - startOfDay(b.start);
      return byDay !== 0 ? byDay : compareEvents(a, b);
    });
}

/** Consecutive runs of the same month, for agenda subheadings. */
export function groupByMonth(events) {
  const groups = [];

  for (const event of events) {
    const key = `${event.start.getFullYear()}-${event.start.getMonth()}`;
    const current = groups[groups.length - 1];

    if (current && current.key === key) {
      current.events.push(event);
    } else {
      groups.push({
        key,
        label: event.start.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        }),
        events: [event],
      });
    }
  }

  return groups;
}

/** "Fri, Sep 11" — the date prefix an event needs in a mixed-day list. */
export function formatEventDay(event) {
  return event.start.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/** Weekday/day/month split out for the agenda's date column. */
export function formatEventDateParts(event) {
  return {
    weekday: event.start.toLocaleDateString("en-US", { weekday: "short" }),
    day: event.start.getDate(),
    month: event.start.toLocaleDateString("en-US", { month: "short" }),
  };
}

/**
 * The inclusive last day of a genuinely multi-day event, or null. A late show
 * that runs past midnight crosses a day boundary but isn't a multi-day event,
 * so only all-day entries and runs of over 24 hours get a range.
 */
export function spanEndDay(event) {
  if (!event.end) return null;
  if (!event.allDay && event.end - event.start <= DAY) return null;

  const last = new Date(event.end.getTime() - 1);
  return isSameDay(last, event.start) ? null : last;
}

export function formatSpan(event) {
  const last = spanEndDay(event);
  if (!last) return null;

  const sameMonth =
    last.getMonth() === event.start.getMonth() &&
    last.getFullYear() === event.start.getFullYear();

  const from = event.start.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const to = last.toLocaleDateString(
    "en-US",
    sameMonth ? { day: "numeric" } : { month: "short", day: "numeric" }
  );

  return `${from}–${to}`;
}

export function formatEventTime(event) {
  if (event.allDay) return "ALL DAY";
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
    .format(event.start)
    .toUpperCase();
}

/**
 * The `dates` parameter for a Google Calendar "add event" link. Google wants an
 * exclusive end in both formats: bare YYYYMMDD for all-day events, a UTC
 * timestamp otherwise. Events with no end get a sensible default rather than a
 * zero-length block.
 */
export function googleCalendarDates(event) {
  const pad = (n) => String(n).padStart(2, "0");
  const asDate = (d) =>
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
  const asUtc = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";

  if (event.allDay) {
    const end = event.end || new Date(event.start.getTime() + DAY);
    return `${asDate(event.start)}/${asDate(end)}`;
  }

  const end = event.end || new Date(event.start.getTime() + HOUR);
  return `${asUtc(event.start)}/${asUtc(end)}`;
}

export function googleCalendarUrl(event) {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: googleCalendarDates(event),
    ...(event.description && { details: event.description }),
    ...(event.location && { location: event.location }),
  });

  return `https://calendar.google.com/calendar/render?${params}`;
}
